/**
 * F3 · Notification center: the bell opens a panel with the full feed.
 * Every notification deep-links to the place it originated from — a take
 * opens in review, a message opens its thread, a lesson opens the calendar.
 * Clicking an item marks it read; the badge tracks unread count.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type ComponentType, type SVGProps } from 'react';
import { useNavigate } from 'react-router';
import { api } from '../lib/api.ts';
import { t } from '../lib/i18n.ts';
import { cls } from '../lib/ui.tsx';
import { IconBell, IconCalendar, IconChat, IconMic, IconSun, IconTrend } from './icons.tsx';

export interface AppNotification {
  id: string;
  type: string;
  title: string;
  body: string;
  refType: string | null;
  refId: string | null;
  readAt: string | null;
  createdAt: string;
}

type Role = 'coach' | 'student';

/** Where a notification originated — resolved per viewer role. */
export function notificationLink(n: AppNotification, role: Role): string | null {
  const coach = role === 'coach';
  switch (n.refType) {
    case 'take':
      return coach ? `/coach/review/${n.refId}` : `/app/takes/${n.refId}`;
    case 'plan':
      return coach ? '/coach' : '/app';
    case 'lesson':
      return coach ? '/coach/calendar' : '/app/lessons';
    case 'thread':
      return coach ? `/coach/messages/${n.refId}` : '/app/messages';
    case 'waitlist':
      return '/app/lessons';
    case 'student':
      return coach ? `/coach/students/${n.refId}` : null;
    case 'review_order':
      return coach ? '/coach/marketplace' : '/app/orders';
  }
  switch (n.type) {
    case 'practice_reminder':
      return '/app';
    case 'digest':
    case 'new_submission':
      return coach ? '/coach' : null;
    case 'new_message':
      return coach ? '/coach/messages' : '/app/messages';
    case 'announcement':
      return coach ? null : '/app/messages';
    case 'lesson_booked':
    case 'lesson_cancelled':
      return coach ? '/coach/calendar' : '/app/lessons';
    default:
      return null;
  }
}

const TYPE_ICONS: Record<string, ComponentType<SVGProps<SVGSVGElement>>> = {
  practice_reminder: IconSun,
  take_reviewed: IconMic,
  new_submission: IconMic,
  new_message: IconChat,
  announcement: IconChat,
  plan_assigned: IconSun,
  lesson_booked: IconCalendar,
  lesson_cancelled: IconCalendar,
  waitlist_offer: IconCalendar,
  digest: IconTrend,
};

function relTime(iso: string): string {
  const sec = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (sec < 60) return t('notif.now', 'just now');
  if (sec < 3600) return `${Math.floor(sec / 60)}m`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h`;
  if (sec < 7 * 86400) return `${Math.floor(sec / 86400)}d`;
  return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

export default function NotificationCenter({ role }: { role: Role }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const panelRef = useRef<HTMLDivElement>(null);

  const { data: notifications } = useQuery({
    queryKey: ['notifications'],
    queryFn: () => api<AppNotification[]>('/api/notifications'),
    refetchInterval: 30_000,
  });
  const unread = (notifications ?? []).filter((n) => !n.readAt).length;

  const markRead = useMutation({
    mutationFn: (ids?: string[]) => api('/api/notifications/read', { body: ids ? { ids } : {} }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['notifications'] }),
  });

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    const onClick = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onClick);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onClick);
    };
  }, [open]);

  function onItemClick(n: AppNotification) {
    if (!n.readAt) markRead.mutate([n.id]);
    setOpen(false);
    const link = notificationLink(n, role);
    if (link) navigate(link);
  }

  return (
    <div className="relative" ref={panelRef}>
      <button
        onClick={() => setOpen((o) => !o)}
        className={cls(
          'relative rounded-full p-2 hover:bg-slate-100',
          open ? 'bg-brand-50 text-brand-700' : 'text-slate-500',
        )}
        aria-label={t('notif.title', 'Notifications')}
        aria-expanded={open}
      >
        <IconBell />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex size-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="fixed inset-x-2 top-14 z-50 sm:absolute sm:inset-x-auto sm:top-11 sm:right-0">
          <div className="max-h-[70vh] w-full overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl sm:w-96">
            <header className="flex items-center justify-between border-b border-slate-100 px-4 py-2.5">
              <h2 className="text-sm font-semibold">{t('notif.title', 'Notifications')}</h2>
              {unread > 0 && (
                <button
                  onClick={() => markRead.mutate(undefined)}
                  className="text-xs font-medium text-brand-700 hover:underline"
                >
                  {t('notif.markAll', 'Mark all read')}
                </button>
              )}
            </header>
            <div className="max-h-[58vh] overflow-y-auto">
              {!notifications || notifications.length === 0 ? (
                <p className="px-4 py-8 text-center text-xs text-slate-400">
                  {t('notif.empty', 'Nothing yet — practice activity and messages land here.')}
                </p>
              ) : (
                <ul>
                  {notifications.map((n) => {
                    const Icon = TYPE_ICONS[n.type] ?? IconBell;
                    const link = notificationLink(n, role);
                    return (
                      <li key={n.id} className="border-b border-slate-50 last:border-0">
                        <button
                          onClick={() => onItemClick(n)}
                          className={cls(
                            'flex w-full items-start gap-3 px-4 py-3 text-left transition-colors',
                            link ? 'hover:bg-brand-50/60' : 'cursor-default',
                            !n.readAt && 'bg-brand-50/40',
                          )}
                        >
                          <span
                            className={cls(
                              'mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full',
                              n.readAt ? 'bg-slate-100 text-slate-400' : 'bg-brand-100 text-brand-700',
                            )}
                          >
                            <Icon width={16} height={16} />
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="flex items-baseline justify-between gap-2">
                              <span className={cls('truncate text-sm', n.readAt ? 'text-slate-600' : 'font-semibold text-slate-900')}>
                                {n.title}
                              </span>
                              <span className="shrink-0 text-[11px] text-slate-400">{relTime(n.createdAt)}</span>
                            </span>
                            {n.body && <span className="mt-0.5 line-clamp-2 block text-xs text-slate-500">{n.body}</span>}
                            {link && <span className="mt-0.5 block text-[11px] font-medium text-brand-600">{t('notif.open', 'Open')} →</span>}
                          </span>
                          {!n.readAt && <span className="mt-2 size-2 shrink-0 rounded-full bg-brand-600" />}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
